import React, {useState} from 'react';

import {useFullVariantByIdSsr} from '../../../api/hooks/useFullVariantByIdSsr.hook';
import {useGlobal} from '../../../api/hooks/useGlobal.hook';
import {useInstanceDetailPage} from '../../../api/hooks/useInstanceDetail.hook';
import {DetailedInstance} from '../../../api/models/detailed-instance';
import {InstanceStatus} from '../../../api/models/instance-status';
import {ResponseCodes} from '../../../utils/_enums/response-codes.enum';
import {SalesType} from '../../../utils/_enums/sales-type';
import {TypographyVariantsEnum} from '../../../utils/_enums/typeography-variants.enum';
import {isDateInFuture, isDateInPast} from '../../../utils/_helper/time-helper';
import {useAppSelector} from '../../../utils/_hooks/hooks';
import LoginDialog from '../login-dialog/login-dialog';
import {Typography} from '../typography/typography';
import SalesBoxFooter from './_shared/sales-box-footer/sales-box-footer';
import CancelResaleBox from './cancel-resale-box/cancel-resale-box';
import FixedPriceSale from './fixed-price-sale/fixed-price-sale';
import MintingBox from './minting-box/minting-box';
import NotAvailableNft from './not-available-nft/not-available-nft';
import OwnerOptions from './owner-options/owner-options';
import styles from './sales-box.module.scss';
import SellNftBox from './sell-ntf-box/sell-nft-box';

export interface SalesBoxProps {
    salesType: SalesType;
}

const SalesBox = (props: SalesBoxProps) => {
    const data = useInstanceDetailPage();
    const globalData = useGlobal();
    const {
        jwt,
        isGeoblocked,
        user,
        walletId,
        pending,
        addToBasketStatus,
        instance
    } = useAppSelector((state) => ({
        jwt: state.authentification.jwt,
        instanceInBasket: state.checkoutSlice.basket,
        user: state.userSlice.user,
        isGeoblocked: state.userSlice.isGeoblocked,
        walletId: state.blockchainSlice.walletId,
        addToBasketStatus: state.checkoutSlice.addToBasketStatus,
        pending: state.checkoutSlice.pending,
        instance: state.productSlice.instance
    }));
    const [instanceIdToBuy, setInstanceIdToBuy] = useState<number | undefined>(undefined);

    const variant = useFullVariantByIdSsr();

    const isOwn = (): boolean => {
        return (
            !!instance &&
            (!!user && user.id === instance.ownerId || walletId === instance.ownerWallet)
        );
    };

    const renderHeadline = () => {
        if (data && instance) {
            if (!jwt
                && instance.status === InstanceStatus.OPEN
                && props.salesType === SalesType.FIXED_PRICE
                && !isDateInPast(variant?.salesAttribute?.saleTo)) {
                return data.fixedPriceHeadline.headlineContent;
            } else if (!jwt && !isOwn()
                || !isOwn() && instance.status !== InstanceStatus.OPEN
                || (!instance.ownerId
                    && !instance.ownerWallet
                    && isDateInPast(variant?.salesAttribute?.saleTo))) {
                return data.notAvailableHeadline.headlineContent;
            } else if (!isOwn() && props.salesType === SalesType.FIXED_PRICE && instance.status === InstanceStatus.OPEN) {
                return data.fixedPriceHeadline.headlineContent;
            } else if (isOwn() && instance.minted && jwt && instance.status === InstanceStatus.SOLD) {
                return data.resaleHeadline.headlineContent;
            } else if (jwt && instance.minted && instance.status !== InstanceStatus.SOLD) {
                return data.abortResaleHeadline.headlineContent;
            } else if (isOwn() && !instance.minted) {
                return data.mintingHeadline.headlineContent;
            } else if (!jwt && isOwn()) {
                return data.ownerOptionsHeadline.headlineContent;
            }
        } else {
            return '';
        }
    }

    const renderSalesBox = (instance: DetailedInstance) => {
        if ((instance.status !== InstanceStatus.OPEN
                || !instance.ownerId
                && !instance.ownerWallet
                && isDateInPast(variant?.salesAttribute?.saleTo))
            && !isOwn()) {
            return <NotAvailableNft/>
        } else if (instance.status === InstanceStatus.OPEN && !isOwn()) {
            return <FixedPriceSale
                isInitial={!instance.ownerId && !instance.ownerWallet}
                instance={instance}
                variant={variant}
                pending={pending || addToBasketStatus === ResponseCodes.OK}
                isGeoblocked={isGeoblocked}
                onBuy={() => setInstanceIdToBuy(instance.id)}
            />
        } else if (!jwt && isOwn()) {
            return <OwnerOptions/>
        } else if (isOwn() && !instance.minted) {
            return <MintingBox/>
        } else if (isOwn() && jwt && instance.minted && instance.status === InstanceStatus.SOLD) {
            return <SellNftBox/>
        } else if (isOwn() && jwt && instance.minted && instance.status === InstanceStatus.OPEN) {
            return <CancelResaleBox instance={instance} variant={variant}/>
        } else {
            return undefined;
        }
    }

    return <div>
        <a className={styles['anchor']} id={'salesbox'}/>
        {data && instance
            ? <div className={styles['sales-box']}>
                <div className={styles['header']}>
                    <div className={styles['sales-title']}>
                        <div className={styles['sales-title-inner']}>
                            <Typography
                                variant={TypographyVariantsEnum.HEADING4_BOLD}
                            >
                                {renderHeadline()}
                            </Typography>
                        </div>
                    </div>
                    {
                        instance.status === InstanceStatus.OPEN
                        && !isOwn()
                        && isDateInFuture(variant?.salesAttribute?.saleTo)
                            ? <Typography
                                variant={TypographyVariantsEnum.BODY}
                                className={styles['price-info-box']}>{!instance.ownerId ? data.buyWithEthOrFiat.content : data.buyWithEth.content}
                            </Typography>
                            : null

                    }
                </div>
                <div className={styles['main-content']}>
                    {renderSalesBox(instance)}
                </div>
                {instance.status === InstanceStatus.OPEN
                && props.salesType === SalesType.FIXED_PRICE
                && !isOwn() ? (
                    <SalesBoxFooter
                        headline={globalData?.salesBox?.feesHeadline}
                        description={globalData?.salesBox?.feesDescription}/>
                ) : undefined}
                <LoginDialog
                    withBuyOption={{
                        setIdToBuy: instanceId => setInstanceIdToBuy(instanceId),
                        idToBuy: instanceIdToBuy,
                        salesType: SalesType.FIXED_PRICE
                    }}
                />
            </div>
            : <div/>}
    </div>
};

export default SalesBox;
